import type { ReactNode, RefObject } from "react";
import styles from "./styles.module.css";
import type { RetrievalGame, RetrievalMediaValue } from "../../store/api/quizRetrieval.types";
import type { QuizCard, QuizContext, QuizDetail, QuizDiagnostic, QuizMediaReference } from "../../store/api/quizChatApi";

interface QuizCardsProps {
  cards: QuizCard[];
  detail: QuizDetail | null;
  detailIds?: number[];
  onDetail: (id: number) => void;
  resultsHeadingRef?: RefObject<HTMLHeadingElement | null>;
  detailHeadingRef?: RefObject<HTMLHeadingElement | null>;
}

const CODE_LABELS: Record<string, string> = {
  INVALID_REQUIRED_FIELDS: "Campos obligatorios inválidos",
  MISSING_CONTEXT_METADATA: "Metadatos de contexto incompletos",
  MISSING_REQUIRED_FIELDS: "Faltan campos obligatorios",
  UNKNOWN_GAME_TYPE: "Tipo de variante desconocido",
  MALFORMED_GAME: "Variante con formato inválido",
  MALFORMED_CONTENT: "Contenido con formato inválido",
  MEDIA_UNAVAILABLE: "Recurso multimedia no disponible",
  MEDIA_FORBIDDEN: "Recurso multimedia sin autorización",
  MODEL_OUTPUT_TRUNCATED: "Respuesta del modelo truncada",
  DEADLINE_EXCEEDED: "Tiempo de espera agotado",
  RESULT_LIMIT_REACHED: "Se alcanzó el límite de resultados",
  EMPTY_ANSWER_KEY: "Variante sin respuesta correcta",
};

const CONTENT_STATUS_LABELS: Record<string, string> = {
  valid: "Contenido válido",
  malformed: "Contenido con formato inválido",
  partial: "Contenido parcial",
  empty: "Contenido vacío",
  unknown: "Estado desconocido",
};

const CONTEXT_FIELDS: Array<{ label: string; read: (context: QuizContext) => string | null }> = [
  { label: "Origen", read: (context) => context.origin },
  { label: "Trimestre", read: (context) => context.trimester },
  { label: "Grado", read: (context) => context.grade },
  { label: "Asignatura", read: (context) => context.subject },
  { label: "Semana", read: (context) => context.week },
  { label: "Lección", read: (context) => context.lesson },
];

const contextValue = (value: string | null) => value ?? "Metadatos no disponibles";

interface CodeGroup {
  code: string;
  count: number;
  detail?: string;
}

const groupWarnings = (codes: string[]): CodeGroup[] => {
  const counts = new Map<string, number>();
  codes.forEach((code) => counts.set(code, (counts.get(code) ?? 0) + 1));
  return Array.from(counts, ([code, count]) => ({ code, count }));
};

const groupDiagnostics = (diagnostics: QuizDiagnostic[]): CodeGroup[] => {
  const counts = new Map<string, { count: number; positions: number[] }>();
  diagnostics.forEach(({ code, index }) => {
    const current = counts.get(code) ?? { count: 0, positions: [] };
    current.count += 1;
    if (index !== null) current.positions.push(index + 1);
    counts.set(code, current);
  });
  return Array.from(counts, ([code, { count, positions }]) => ({
    code,
    count,
    detail: positions.length > 0 ? `Variantes: ${positions.join(", ")}` : undefined,
  }));
};

const groupUnavailableMedia = (media: QuizMediaReference[]): CodeGroup[] => {
  const counts = new Map<string, { count: number; sources: string[] }>();
  media
    .filter(({ status }) => status === "unavailable")
    .forEach(({ diagnostic, source }) => {
      const code = diagnostic ?? "MEDIA_UNAVAILABLE";
      const current = counts.get(code) ?? { count: 0, sources: [] };
      current.count += 1;
      current.sources.push(source);
      counts.set(code, current);
    });
  return Array.from(counts, ([code, { count, sources }]) => ({ code, count, detail: sources.join(" · ") }));
};

const CodeBadges = ({ title, groups, tone }: { title: string; groups: CodeGroup[]; tone?: string }) =>
  groups.length === 0 ? null : (
    <section className={styles.badgeSection}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      <ul className={styles.badgeList}>
        {groups.map(({ code, count, detail }) => (
          <li key={code} className={`${styles.badge} ${tone ?? ""}`.trim()} title={code}>
            {CODE_LABELS[code] && <span className={styles.badgeLabel}>{CODE_LABELS[code]}</span>}
            <code className={styles.badgeCode}>{code}</code>
            {detail && <span className={styles.badgeDetail}>{detail}</span>}
            {count > 1 && <span className={styles.badgeCount}>{`×${count}`}</span>}
          </li>
        ))}
      </ul>
    </section>
  );

const RawContent = ({ value }: { value: unknown }) => {
  let formatted: string;
  try {
    formatted = JSON.stringify(value, null, 2);
  } catch {
    formatted = String(value);
  }
  return (
    <details className={styles.disclosure}>
      <summary className={styles.disclosureSummary}>Contenido original sin modificar</summary>
      <pre className={styles.rawContent}>{formatted}</pre>
    </details>
  );
};

const ContextList = ({ label, contexts, heading }: { label: string; contexts: QuizContext[]; heading?: string }) => (
  <section className={styles.contextSection} aria-label={label}>
    {heading && <h4 className={styles.sectionTitle}>{heading}</h4>}
    {contexts.length === 0 ? (
      <p className={styles.emptyNote}>Sin contextos.</p>
    ) : (
      <ul className={styles.contextList}>
        {contexts.map((context, index) => (
          <li key={`${label}-${index}`} className={styles.contextRow}>
            <dl className={styles.contextFields}>
              {CONTEXT_FIELDS.map(({ label: field, read }) => (
                <div key={field} className={styles.contextField}>
                  <dt className={styles.contextFieldLabel}>{field}</dt>
                  <dd className={styles.contextFieldValue}>{contextValue(read(context))}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const ContextDisclosure = ({ label, contexts }: { label: string; contexts: QuizContext[] }) => (
  <details className={styles.disclosure}>
    <summary className={styles.disclosureSummary}>{`${label} (${contexts.length})`}</summary>
    <div className={styles.disclosureBody}>
      <ContextList label={label} contexts={contexts} />
    </div>
  </details>
);

type AuthorizedImages = Map<string, string>;

const AuthorizedImage = ({ source, images, alt }: { source?: string; images: AuthorizedImages; alt: string }) => {
  if (!source) return null;
  const url = images.get(source);
  return url ? (
    <img className={styles.mediaImage} src={url} alt={alt} />
  ) : (
    <p className={styles.mediaNote}>{`Imagen no disponible: ${source}`}</p>
  );
};

const AudioReference = ({ source }: { source?: string }) =>
  source ? <p className={styles.mediaNote}>{`Referencia de audio (sin URL autorizada): ${source}`}</p> : null;

const MediaValue = ({ value, images, label }: { value: RetrievalMediaValue; images: AuthorizedImages; label: string }) => (
  <>
    {value.text !== undefined && <p className={styles.itemText}>{value.text}</p>}
    <AuthorizedImage source={value.image} images={images} alt={`Imagen: ${label}`} />
    <AudioReference source={value.audio} />
  </>
);

const Item = ({ id, children, correct }: { id: string; children: ReactNode; correct?: boolean }) => (
  <li className={`${styles.item} ${correct ? styles.itemCorrect : ""}`.trim()}>
    <p className={styles.itemId}>{`ID: ${id}`}</p>
    <div className={styles.itemBody}>{children}</div>
  </li>
);

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className={styles.group}>
    <h5 className={styles.groupTitle}>{title}</h5>
    <ul className={styles.itemList} aria-label={title}>
      {children}
    </ul>
  </div>
);

const Fact = ({ children }: { children: ReactNode }) => <p className={styles.fact}>{children}</p>;

const GameBody = ({ game, images }: { game: RetrievalGame; images: AuthorizedImages }) => {
  switch (game.type) {
    case "multiple_choice":
      return (
        <Group title="Opciones">
          {game.options.map((option) => (
            <Item key={option.id} id={option.id} correct={option.correct}>
              <MediaValue value={option} images={images} label={option.id} />
              <Fact>{`Correcto: ${option.correct ? "sí" : "no"}`}</Fact>
            </Item>
          ))}
        </Group>
      );
    case "true_false":
      return (
        <>
          <Group title="Categorías">
            {game.categories.map((category) => (
              <Item key={category.id} id={category.id}>
                <p className={styles.itemText}>{category.label}</p>
                <AudioReference source={category.audio} />
              </Item>
            ))}
          </Group>
          <Group title="Enunciados">
            {game.statements.map((statement) => (
              <Item key={statement.id} id={statement.id}>
                <MediaValue value={statement} images={images} label={statement.id} />
                <Fact>{`Categoría correcta: ${statement.correctCategoryId}`}</Fact>
              </Item>
            ))}
          </Group>
        </>
      );
    case "drag_and_drop":
    case "drag_and_drop_horizontal":
      return (
        <Group title="Elementos">
          {game.items.map((item) => (
            <Item key={item.id} id={item.id}>
              <MediaValue value={item} images={images} label={item.id} />
              {"slotLabel" in item && item.slotLabel !== undefined && <Fact>{`Casilla: ${item.slotLabel}`}</Fact>}
            </Item>
          ))}
        </Group>
      );
    case "fill_in_blanks":
      return (
        <>
          <p className={styles.gameText}>{game.text}</p>
          <AudioReference source={game.textAudio} />
          <Group title="Espacios">
            {game.blanks.map((blank) => (
              <Item key={blank.id} id={blank.id}>
                <Fact>{`Posición: ${blank.position}`}</Fact>
                <Fact>{`Opción correcta: ${blank.correctOptionId}`}</Fact>
              </Item>
            ))}
          </Group>
          <Group title="Opciones">
            {game.options.map((option) => (
              <Item key={option.id} id={option.id}>
                <MediaValue value={option} images={images} label={option.id} />
              </Item>
            ))}
          </Group>
        </>
      );
    case "categorization":
      return (
        <>
          <Group title="Categorías">
            {game.categories.map((category) => (
              <Item key={category.id} id={category.id}>
                <p className={styles.itemText}>{category.name}</p>
                <AudioReference source={category.audio} />
              </Item>
            ))}
          </Group>
          <Group title="Elementos">
            {game.items.map((item) => (
              <Item key={item.id} id={item.id}>
                <MediaValue value={item} images={images} label={item.id} />
                <Fact>{`Categoría: ${item.categoryId}`}</Fact>
              </Item>
            ))}
          </Group>
          {game.distractors && (
            <Group title="Distractores">
              {game.distractors.map((item) => (
                <Item key={item.id} id={item.id}>
                  <MediaValue value={item} images={images} label={item.id} />
                </Item>
              ))}
            </Group>
          )}
        </>
      );
    case "matching":
      return (
        <>
          <Group title="Pares">
            {game.pairs.map((pair) => (
              <Item key={pair.id} id={pair.id}>
                <div className={styles.pair}>
                  <div className={styles.pairSide}>
                    <p className={styles.pairLabel}>Izquierda</p>
                    <MediaValue value={pair.left} images={images} label={`${pair.id} izquierda`} />
                  </div>
                  <div className={styles.pairSide}>
                    <p className={styles.pairLabel}>Derecha</p>
                    <MediaValue value={pair.right} images={images} label={`${pair.id} derecha`} />
                  </div>
                </div>
              </Item>
            ))}
          </Group>
          {game.distractors && (
            <Group title="Distractores de emparejamiento">
              {game.distractors.map((item) => (
                <Item key={item.id} id={item.id}>
                  <MediaValue value={item} images={images} label={item.id} />
                </Item>
              ))}
            </Group>
          )}
        </>
      );
    case "text_input":
      return (
        <>
          <Fact>{`Distingue mayúsculas: ${game.caseSensitive ? "sí" : "no"}`}</Fact>
          <Fact>{`Evalúa acentuación: ${game.accentSensitive ? "sí" : "no"}`}</Fact>
          <Group title="Respuestas de referencia (califica la IA)">
            {game.correctAnswers.map((answer) => (
              <Item key={answer.id} id={answer.id} correct>
                <p className={styles.itemText}>{answer.text}</p>
                <AudioReference source={answer.audio} />
              </Item>
            ))}
          </Group>
        </>
      );
    case "text_input_ai":
      return (
        <div className={styles.factList}>
          <Fact>{`Respuesta de referencia: ${game.modelAnswer}`}</Fact>
          <Fact>{`Ideas clave: ${game.keyIdeas}`}</Fact>
          {game.keywords !== undefined && <Fact>{`Palabras clave: ${game.keywords}`}</Fact>}
          {game.subject !== undefined && <Fact>{`Asignatura: ${game.subject}`}</Fact>}
          {game.grade !== undefined && <Fact>{`Grado: ${game.grade}`}</Fact>}
        </div>
      );
    case "image_labeling":
      return (
        <>
          <AudioReference source={game.imageAudio} />
          <Group title="Zonas">
            {game.hotspots.map((hotspot) => (
              <Item key={hotspot.id} id={hotspot.id}>
                <div className={styles.coordinates}>
                  <Fact>{`X: ${hotspot.x}`}</Fact>
                  <Fact>{`Y: ${hotspot.y}`}</Fact>
                  <Fact>{`Ancho: ${hotspot.width}`}</Fact>
                  <Fact>{`Alto: ${hotspot.height}`}</Fact>
                </div>
              </Item>
            ))}
          </Group>
          <Group title="Etiquetas">
            {game.labels.map((label) => (
              <Item key={label.id} id={label.id}>
                <MediaValue value={label} images={images} label={label.id} />
                <Fact>{`Zona correcta: ${label.correctHotspotId}`}</Fact>
              </Item>
            ))}
          </Group>
        </>
      );
    case "image_hotspot":
      return (
        <>
          <AudioReference source={game.imageAudio} />
          <Group title="Zonas">
            {game.hotspots.map((hotspot) => (
              <Item key={hotspot.id} id={hotspot.id} correct={hotspot.correct}>
                <div className={styles.coordinates}>
                  <Fact>{`X: ${hotspot.x}`}</Fact>
                  <Fact>{`Y: ${hotspot.y}`}</Fact>
                  <Fact>{`Ancho: ${hotspot.width}`}</Fact>
                  <Fact>{`Alto: ${hotspot.height}`}</Fact>
                  <Fact>{`Correcto: ${hotspot.correct ? "sí" : "no"}`}</Fact>
                </div>
              </Item>
            ))}
          </Group>
        </>
      );
    case "text_selection":
      return (
        <>
          <p className={styles.gameText}>{game.text}</p>
          <AudioReference source={game.textAudio} />
          <Fact>{`Modo de autoría: ${game.authoringMode}`}</Fact>
          <Group title="Partes">
            {game.parts.map((part) => (
              <Item key={part.id} id={part.id} correct={part.correct}>
                <p className={styles.itemText}>{part.text}</p>
                <div className={styles.coordinates}>
                  <Fact>{`Seleccionable: ${part.selectable ? "sí" : "no"}`}</Fact>
                  <Fact>{`Correcto: ${part.correct ? "sí" : "no"}`}</Fact>
                  {part.trailing !== undefined && <Fact>{`Final: ${part.trailing}`}</Fact>}
                </div>
              </Item>
            ))}
          </Group>
        </>
      );
    default:
      return null;
  }
};

const GameRenderer = ({ game, index, images }: { game: RetrievalGame; index: number; images: AuthorizedImages }) => (
  <article className={styles.game} data-game-type={game.type} aria-label={`Variante ${index + 1}: ${game.question}`}>
    <header className={styles.gameHeader}>
      <p className={styles.gameIndex}>{index + 1}</p>
      <h4 className={styles.gameQuestion}>{game.question}</h4>
    </header>
    <div className={styles.metaRow}>
      <p className={styles.metaPill}>{`Tipo: ${game.type}`}</p>
      <p className={styles.metaPill}>{`ID original: ${game.id}`}</p>
      <p className={styles.metaPill}>{`Puntos: ${game.points}`}</p>
    </div>
    <AuthorizedImage source={game.image} images={images} alt="Imagen de la pregunta" />
    <AudioReference source={game.questionAudio} />
    <GameBody game={game} images={images} />
  </article>
);

const authorizedImages = (media: QuizMediaReference[]): AuthorizedImages =>
  new Map(media.flatMap((entry) => (entry.status === "available" && entry.url ? [[entry.source, entry.url] as const] : [])));

export function QuizCards({ cards, detail, detailIds = [], onDetail, resultsHeadingRef, detailHeadingRef }: QuizCardsProps) {
  const displayedIds = new Set(cards.map(({ quiz_id }) => quiz_id));
  const actionIds = detailIds.filter((id) => !displayedIds.has(id));
  const images = authorizedImages(detail?.media ?? []);
  const games = detail?.normalized_games ?? [];
  const contentStatusLabel = detail ? CONTENT_STATUS_LABELS[detail.content_status] : undefined;
  return (
    <section className={styles.results} aria-label="Resultados canónicos">
      <h2 className={styles.resultsHeading} ref={resultsHeadingRef} tabIndex={-1}>
        Resultados canónicos
      </h2>
      {cards.map((card) => (
        <article key={card.quiz_id} className={styles.quizCard} aria-label={`Quiz ${card.quiz_id}`}>
          <header className={styles.quizCardHeader}>
            <h3 className={styles.quizTitle}>{card.title}</h3>
            <p className={styles.quizId}>{`Quiz ID: ${card.quiz_id}`}</p>
            <button type="button" className={styles.detailButton} onClick={() => onDetail(card.quiz_id)}>
              {`View quiz ${card.quiz_id}`}
            </button>
          </header>
          <ContextList label="Contextos coincidentes" heading="Contextos coincidentes" contexts={card.matching_contexts} />
          <ContextDisclosure label="Todos los contextos" contexts={card.contexts} />
          <CodeBadges title="Avisos" groups={groupWarnings(card.warnings)} />
        </article>
      ))}
      {actionIds.length > 0 && (
        <div className={styles.actionRow}>
          {actionIds.map((id) => (
            <button key={id} type="button" className={styles.detailButton} onClick={() => onDetail(id)}>
              {`View quiz ${id}`}
            </button>
          ))}
        </div>
      )}
      {detail && (
        <section className={styles.detailPanel} aria-label={`Quiz ${detail.quiz_id} detail`}>
          <header className={styles.detailHeader}>
            <h2 className={styles.detailTitle} ref={detailHeadingRef} tabIndex={-1}>
              {`Detalle canónico: ${detail.title}`}
            </h2>
            <p className={styles.statusPill} data-status={detail.content_status}>
              <span className={styles.statusLabel}>Estado del contenido</span>
              <span className={styles.statusValue}>{contentStatusLabel ?? detail.content_status}</span>
              {contentStatusLabel && <code className={styles.statusCode}>{detail.content_status}</code>}
            </p>
          </header>
          <ContextDisclosure label="Contextos del detalle" contexts={detail.contexts} />
          <CodeBadges title="Avisos y diagnósticos" groups={[...groupWarnings(detail.warnings), ...groupDiagnostics(detail.diagnostics)]} />
          <CodeBadges title="Recursos no disponibles" groups={groupUnavailableMedia(detail.media)} tone={styles.badgeDanger} />
          {games.length > 0 ? (
            <section className={styles.gamesSection}>
              <h3 className={styles.sectionHeading}>{`Variantes del quiz (${games.length})`}</h3>
              <ol className={styles.gameList}>
                {games.map(({ index, game }) => (
                  <li key={game.id} className={styles.gameItem}>
                    <GameRenderer game={game} index={index} images={images} />
                  </li>
                ))}
              </ol>
            </section>
          ) : (
            <p className={styles.emptyNote}>Este quiz no tiene variantes normalizadas.</p>
          )}
          <RawContent value={detail.raw_games} />
        </section>
      )}
    </section>
  );
}
